import { useEffect, useRef } from "react";
import { readConfig } from "../../../lib/config";

/**
 * 문 B(Campus BFF SSO) 토큰 전달 — ARC 는 "BFF 토큰 SSO(Bench ADR-B11/B12 세 진입 경로)"만 정하고
 * 브라우저 전달 방식은 정하지 않았다. 셸은 두 가지를 받는다:
 *
 *  1) `/sso#bffToken=<t>` — 프래그먼트(서버 로그·Referer 에 안 남음). 부팅 시 읽고 즉시 지운다.
 *  2) postMessage — Campus SPA 가 Field 를 새 창(window.open) 또는 iframe 으로 열고,
 *     셸이 `{type:"picnic-field/sso-ready"}` 를 opener/parent 로 보내면 Campus 가
 *     `{type:"picnic-field/bff-token", bffToken}` 을 셸 출처로 되돌려 보낸다.
 *     허용 출처는 VITE_CAMPUS_ORIGINS(쉼표 구분). 목록 밖 출처·형식이 다른 메시지는 버린다.
 *
 * 쿼리 문자열(?bffToken=)은 서버 로그·Referer 로 새므로 받지 않는다.
 */
export const SSO_READY = "picnic-field/sso-ready";
export const SSO_TOKEN = "picnic-field/bff-token";

export function acceptSsoMessage(
  ev: Pick<MessageEvent, "origin" | "data">,
  allowed: readonly string[],
): string | null {
  if (!allowed.includes(ev.origin)) return null;
  const d = ev.data as { type?: unknown; bffToken?: unknown } | null;
  if (!d || d.type !== SSO_TOKEN || typeof d.bffToken !== "string" || !d.bffToken) return null;
  return d.bffToken;
}

const CONFIGURED_ORIGINS = readConfig().campusOrigins;

export function useCampusSsoMessage(
  active: boolean,
  onToken: (token: string) => void,
  allowed: readonly string[] = CONFIGURED_ORIGINS,
) {
  const cb = useRef(onToken);
  useEffect(() => {
    cb.current = onToken;
  });
  useEffect(() => {
    if (!active) return;
    let done = false;
    const onMessage = (ev: MessageEvent) => {
      const token = acceptSsoMessage(ev, allowed);
      if (!token || done) return;
      done = true;
      cb.current(token);
    };
    window.addEventListener("message", onMessage);
    const peer = window.opener ?? (window.parent !== window ? window.parent : null);
    if (peer) for (const o of allowed) peer.postMessage({ type: SSO_READY }, o);
    return () => window.removeEventListener("message", onMessage);
  }, [active, allowed]);
}
